/**
 * CurrentStatusCard - the pinned "where does this ticket stand right
 * now" panel at the top of the agent detail page (SPEC section 9).
 *
 * Greg 2026-07-30: the AI support agent posted a "Support Session
 * Summary" internal note after every pass, so a worked ticket carried
 * a dozen of them and answering "what is the situation" meant finding
 * the newest one. This is ONE value the AI overwrites instead, pinned
 * where he reads it first.
 *
 * Agent-only, like an internal note - hence the amber treatment, which
 * is what "internal, not for the customer" looks like everywhere else
 * in this app. It is structurally absent from the customer surface:
 * the field only exists on the backend's AgentTicketResponse.
 *
 * Always mounted, even with nothing in it, so the empty state is the
 * prompt to write one. Expanded on load, collapsible by hand (the
 * collapse is deliberately per-visit state, not a saved preference -
 * "always expanded when the ticket loads" was the ask).
 */
import { useEffect, useState } from "react";

import { Alert, Button, Card, Spinner } from "@/components/ui";
import { RichTextEditor } from "@/components/tickets/RichTextEditor";
import { safeHtmlProps } from "@/lib/sanitize";

/** Relative age of the last update. The value is only useful if it
 * tracks the present, so staleness is the thing worth showing. */
function relativeAge(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.round((Date.now() - then) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

export function CurrentStatusCard({
  html,
  updatedAt,
  onSave,
  isSaving,
  saveError,
  canEdit = true,
}: {
  html: string | null | undefined;
  updatedAt: string | null | undefined;
  /** Persist the new body. Empty string clears the field. */
  onSave: (html: string) => Promise<void> | void;
  isSaving: boolean;
  saveError: string | null;
  canEdit?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draftHtml, setDraftHtml] = useState(html ?? "");
  const [draftText, setDraftText] = useState("");

  // Re-seed the editor when a different ticket loads, or when the AI
  // writes a new status while the card sits idle. Deliberately NOT
  // while editing: the 15s detail poll would otherwise wipe whatever
  // the agent is typing.
  useEffect(() => {
    if (!editing) setDraftHtml(html ?? "");
  }, [html, editing]);

  const hasStatus = Boolean(html && html.trim());

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="-ml-1 rounded p-1 text-neutral-500 hover:bg-amber-100"
          aria-expanded={open}
          aria-label={open ? "Collapse current status" : "Expand current status"}
          data-testid="current-status-toggle"
        >
          <i
            className={`fa-duotone fa-thin ${
              open ? "fa-chevron-down" : "fa-chevron-right"
            }`}
            aria-hidden="true"
          />
        </button>
        <h2 className="text-sm font-semibold text-neutral-700">
          Current Status
        </h2>
        <span className="inline-flex items-center rounded-full bg-amber-200 px-2 py-0.5 text-xs font-medium text-amber-800">
          Internal
        </span>
        {updatedAt && hasStatus && (
          <span
            className="text-xs text-neutral-400"
            data-testid="current-status-age"
          >
            updated {relativeAge(updatedAt)}
          </span>
        )}
        {canEdit && !editing && (
          <span className="ml-auto">
            <button
              type="button"
              onClick={() => {
                setDraftHtml(html ?? "");
                setOpen(true);
                setEditing(true);
              }}
              className="rounded px-1.5 py-0.5 text-xs text-neutral-600 hover:bg-amber-100 hover:text-amber-900"
              data-testid="current-status-edit"
            >
              <i
                className="fa-duotone fa-thin fa-pen-to-square mr-1"
                aria-hidden="true"
              />
              Edit
            </button>
          </span>
        )}
      </div>

      {open && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          {editing ? (
            <div className="space-y-2">
              <RichTextEditor
                value={draftHtml}
                onChange={(nextHtml, nextText) => {
                  setDraftHtml(nextHtml);
                  setDraftText(nextText);
                }}
                placeholder="Where does this ticket stand right now?"
                ariaLabel="Current status body"
              />
              {saveError && <Alert variant="error">{saveError}</Alert>}
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700 active:bg-amber-800"
                  data-testid="current-status-save"
                  onClick={() => {
                    // An editor emptied by hand still emits markup
                    // ("<p></p>"); send "" so the backend stores a
                    // clean NULL rather than an empty paragraph.
                    const body = draftText.trim() ? draftHtml : "";
                    void Promise.resolve(onSave(body))
                      .then(() => {
                        setEditing(false);
                      })
                      // Stay in the editor on failure - the parent has
                      // already put the reason in `saveError`, and the
                      // agent should not lose what they typed.
                      .catch(() => undefined);
                  }}
                  isLoading={isSaving}
                >
                  Save
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isSaving}
                  onClick={() => {
                    setDraftHtml(html ?? "");
                    setEditing(false);
                  }}
                >
                  Cancel
                </Button>
                {isSaving && <Spinner />}
              </div>
            </div>
          ) : hasStatus ? (
            <div
              data-testid="current-status-body"
              className="prose prose-sm max-w-none text-sm text-neutral-800"
              dangerouslySetInnerHTML={safeHtmlProps(html ?? "")}
            />
          ) : (
            <p
              className="text-sm text-neutral-500"
              data-testid="current-status-empty"
            >
              No current status yet. The AI agent writes this after each
              update, and you can edit it here.
            </p>
          )}
        </div>
      )}
    </Card>
  );
}
